import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Provider {
  id: string
  name: string
  type: string
  status: string
  tenant_id: string
  users_synced: number
  last_sync: string | null
}

interface LoginEvent {
  id: string
  provider_id: string
  user_email: string
  status: string
  timestamp: string
  error?: string
}

export default function SSOConfigPage() {
  const [providers, setProviders] = useState<Provider[]>([])
  const [loginEvents, setLoginEvents] = useState<LoginEvent[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [provRes, eventsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/sso/providers'),
      fetch(API_BASE + '/sso/login-events'),
      fetch(API_BASE + '/sso/statistics')
    ])
    if (provRes.ok) setProviders((await provRes.json()).providers || [])
    if (eventsRes.ok) setLoginEvents((await eventsRes.json()).events || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const testConnection = async (providerId: string) => {
    const res = await fetch(API_BASE + '/sso/providers/' + providerId + '/test', { method: 'POST' })
    if (res.ok) alert('Connection successful!')
  }

  const syncUsers = async (providerId: string) => {
    await fetch(API_BASE + '/sso/providers/' + providerId + '/sync', { method: 'POST' })
    fetchData()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'success': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'saml': return '🔐'
      case 'oidc': return '🔑'
      case 'oauth2': return '🌐'
      default: return '🔒'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">SSO/SAML Configuration</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Providers</div>
              <div className="text-2xl font-bold">{statistics.total_providers}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_providers}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Users Synced</div>
              <div className="text-2xl font-bold">{statistics.total_users_synced}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Logins Today</div>
              <div className="text-2xl font-bold">{statistics.logins_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Success Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.success_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h2 className="text-xl font-semibold">Identity Providers</h2>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Provider</button>
              </div>
              <div className="divide-y">
                {providers.map(prov => (
                  <div key={prov.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedProvider(prov)}>
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getTypeIcon(prov.type)}</span>
                        <div>
                          <div className="font-semibold">{prov.name}</div>
                          <div className="text-sm text-gray-500 uppercase">{prov.type}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(prov.status)}`}>{prov.status}</span>
                        <button onClick={(e) => { e.stopPropagation(); testConnection(prov.id); }} className="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200">Test</button>
                        <button onClick={(e) => { e.stopPropagation(); syncUsers(prov.id); }} className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200">Sync</button>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-2 text-sm text-gray-500">
                      <span>{prov.users_synced} users synced</span>
                      {prov.last_sync && <span>Last sync: {new Date(prov.last_sync).toLocaleString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Login Events</h2></div>
              <div className="divide-y">
                {loginEvents.slice(0, 10).map(event => (
                  <div key={event.id} className="p-4 flex justify-between items-center">
                    <div>
                      <div className="font-medium">{event.user_email}</div>
                      <div className="text-sm text-gray-500">{new Date(event.timestamp).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(event.status)}`}>{event.status}</span>
                      {event.error && <span className="text-xs text-red-600">{event.error}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            {selectedProvider ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{getTypeIcon(selectedProvider.type)}</span>
                  <h3 className="text-lg font-semibold">{selectedProvider.name}</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500">Type</div>
                    <div className="font-medium uppercase">{selectedProvider.type}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Status</div>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedProvider.status)}`}>{selectedProvider.status}</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Users Synced</div>
                    <div className="font-medium">{selectedProvider.users_synced}</div>
                  </div>
                  <div className="pt-4 space-y-2">
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Edit Configuration</button>
                    <button className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50">View Logs</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Provider Details</h3>
                <p className="text-gray-500 text-sm">Select a provider to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
